import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { DialogDocumentsKriaOverviewList } from 'app/dialogs/documents/kria/dialog-documents-kria-overviewList';
import { TranslocoLocaleWrapperService } from 'app/fuse-template/core/transloco/translocoLocale.service';
import { formatDate } from 'app/helpers/formatDate';
import { formatKennitala } from 'app/helpers/formatKennitala';
import { DocumentsDivisionDefaultValues } from 'app/interfaces/documents/documentsDivisionDefaultValues';
import { ExposedCreateDocumentReq } from 'app/interfaces/requests/Exposed/exposedCreateDocumentReq';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { ListBrefLoggurHoparRes } from 'app/interfaces/responses/List/listBrefLoggurHoparRes';
import { ListBrefLoggurHopurRes } from 'app/interfaces/responses/List/listBrefLoggurHopurRes';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { SkjalamidjaService } from 'app/services/api/skjalamidja/skjalamidja.service';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import {
    DocumentsAddGeneralInfoComponent,
    DocumentsAddGeneralInfoValue,
} from '../../../documents-add-general-info/documents-add-general-info.component';

@Component({
    selector: 'documents-add-multiple-system-kria',
    templateUrl: './documents-add-multiple-system-kria.component.html',
})
export class DocumentsAddMultipleSystemKriaComponent implements OnInit {
    @ViewChild(DocumentsAddGeneralInfoComponent)
    generalInfoComponent: DocumentsAddGeneralInfoComponent;

    form: UntypedFormGroup;
    formEnabled = false;
    submitted = false;
    files: File[] = [];

    brefautskriftirHoparDropDown: Array<ListBrefLoggurHoparRes> = [];
    brefLoggurValinnHopur: Array<ListBrefLoggurHopurRes> = [];

    countCreateDoc = 0;
    createDocErrors: string[] = [];
    userDetails: ExposedReadUserDetailsRes = null;
    userIdentityName: string;
    validFilesAgainstKria = false;
    pdfFiles = true;
    simplifiedEntraUserId: string = '';

    constructor(
        private route: ActivatedRoute,
        private _fuseConfirmationService: FuseConfirmationService,
        private formBuilder: UntypedFormBuilder,
        private lsrauthService: LsrauthService,
        private _formBuilder: UntypedFormBuilder,
        private skjalamidja: SkjalamidjaService,
        private alertsService: AlertsService,
        private router: Router,
        public dialog: MatDialog,
        private localeService: TranslocoLocaleWrapperService
    ) {
        this.lsrauthService.userDetails.subscribe((userDetails) => {
            this.userDetails = userDetails;
            // Drop lsr prefix from entraUserId for comparison, as all usernames in Kría have been updated to no longer include the 'lsr.' prefix.
            // Used when comparing the user's Entra ID with Kría usernames.
            this.simplifiedEntraUserId = this.userDetails?.user?.entraUserId?.toLowerCase().replace('lsr.', '') ?? '';
        });
    }

    ngOnInit(): void {
        this.initForm();
        this.initDropdowns();
    }

    initDropdowns() {
        var [categoriesRes, subcategoriesRes, systemsRes, typesRes, departmentsRes, documentTemplates] =
            this.route.snapshot.parent.data.dropdownData;

        let kria = systemsRes.find((x) => (x.title = 'Kría'));
        this.form.get('documentSystemId').setValue(kria.documentSystemId);

        // Sets default value
        this.form.patchValue({ documentSystemId: kria.documentSystemId });
    }

    // Temp path location which Kria always created on the user's local computer.
    findKriaFolderPath() {
        return (
            'C:\\Users\\' +
            this.userDetails.user.entraUserId +
            '\\AppData\\Local\\Temp\\KriaTemp' +
            this.simplifiedEntraUserId.toUpperCase()
        );
    }

    initForm() {
        this.submitted = false;
        this.form = this._formBuilder.group({
            generalInfo: new FormControl(''),
            documentSystemId: new FormControl(null, [Validators.required]),
            step1Upload: this._formBuilder.group({
                check: [false, [Validators.requiredTrue]],
            }),
            step2Upload: this._formBuilder.group({
                check: [false, [Validators.requiredTrue]],
            }),
            step3Upload: this._formBuilder.group({
                kriaHopurId: new UntypedFormControl(null, [Validators.required]),
            }),
            step4Upload: this._formBuilder.group({
                check: [false, [Validators.requiredTrue]],
            }),
            step5Upload: this._formBuilder.group({}),
        });

        this.files = [];
        this.formEnabled = true;
    }

    revertSingleDocAllChanges() {
        if (confirm('Ertu viss um að þú viljir hætta við?')) {
            this.form.markAsPristine();
            this.initForm();
        }
    }

    formatKennitala(kt) {
        return formatKennitala(kt);
    }

    formatDate(date) {
        return formatDate(date);
    }

    brefaUtsriftSelect(value) {
        let brefabunki = this.brefautskriftirHoparDropDown.find((x) => x.dropDown.value == value);
        if (brefabunki.heiti.length <= 80) {
            this.form.patchValue({
                generalInfo: {
                    ...this.form.get('generalInfo').value,
                    title: brefabunki.heiti,
                },
            });
        }

        this.skjalamidja
            .listBrefLoggurHopur({
                Heiti: brefabunki.heiti,
                Notandi: brefabunki.notandi,
                Sjodur: brefabunki.sjodur,
                SkjalIdHopur: brefabunki.skjaliD_HOPUR,
                SkradDags: brefabunki.skraD_DAGS,
            })
            .subscribe((res) => {
                if (res != null) {
                    this.brefLoggurValinnHopur = res;
                }
            });
    }

    //#region HTTP

    listBrefLoggurHopar() {
        var dagsFra = new Date();
        dagsFra.setDate(dagsFra.getDate() - 2);
        dagsFra.setHours(0, 0, 0, 0);
        var dagsTil = new Date();
        dagsTil.setDate(dagsTil.getDate() + 1);

        this.brefautskriftirHoparDropDown = [];
        this.skjalamidja.listBrefLoggurHopar({ DagsFra: dagsFra, DagsTil: dagsTil }).subscribe((res) => {
            // Makes sure to only list bréfaútskriftir from the current user.
            res = res.filter((x) => x.notandi.toLowerCase().includes(this.simplifiedEntraUserId));
            for (let i = 0; i < res.length; i++) {
                this.brefautskriftirHoparDropDown[i] = {
                    dropDown: {
                        value: i.toString(),
                        viewValue:
                            '<strong>' +
                            formatDate(res[i].skraD_DAGS, false) +
                            ', ' +
                            res[i].notandi +
                            ', ' +
                            res[i].sjodur +
                            '</strong> | <span>' +
                            res[i].heiti +
                            '</span>',
                    },
                    heiti: res[i].heiti,
                    notandi: res[i].notandi,
                    sjodur: res[i].sjodur,
                    skjaliD_HOPUR: res[i].skjaliD_HOPUR,
                    skraD_DAGS: res[i].skraD_DAGS,
                };
            }
        });
    }

    save() {
        this.generalInfoComponent.form.markAllAsTouched();

        this.submitted = true;
        if (!this.form.valid) {
            this.alertsService.changealertHeader('Villur í formi');
            this.alertsService.changeAlertErrorMessage('Vinsamlegast leysið úr villum í formi');
            this.alertsService.changeAlertErrorShow(true);
            return;
        }
        this.form.disable();
        this.formEnabled = false;
        this.storeSelectedValues();

        var reqArr: Array<ExposedCreateDocumentReq> = [];
        var generalInfo: DocumentsAddGeneralInfoValue = this.generalInfoComponent.form.value;

        this.brefLoggurValinnHopur.forEach((skjal) => {
            reqArr.push({
                azureLocation: null,
                description: generalInfo.description,
                documentCategoryId: generalInfo.documentCategoryId,
                documentDate: skjal.dags,
                documentSubcategoryId: generalInfo.documentSubcategoryId,
                documentSystemId: this.form.get('documentSystemId').value,
                documentTypeId: generalInfo.documentTypeId,
                fileExtension: null,
                fileName: skjal.slod,
                publicationDate: generalInfo.publicationDate,
                Ssn: skjal.ktalas,
                stafraentIslandId: null,
                submitToStafraentIsland: generalInfo.submitToStafraentIsland ? generalInfo.submitToStafraentIsland : false,
                timeStamp: null,
                title: generalInfo.title,
                svid: generalInfo.svid,
                deild: generalInfo.deild,
                submitToPortalLSR: generalInfo.submitToPortalLSR,
            });
        });
        this.countCreateDoc = 0;
        this.syncCallAddBref(reqArr);
    }

    /**
     * Makes sure to call the endpoint synchronously
     */
    syncCallAddBref(reqArr: Array<ExposedCreateDocumentReq>) {
        if (this.countCreateDoc <= reqArr.length) {
            this.skjalamidja
                .uploadDocument(
                    {
                        azureLocation: null,
                        description: reqArr[this.countCreateDoc].description,
                        documentCategoryId: reqArr[this.countCreateDoc].documentCategoryId,
                        documentDate: reqArr[this.countCreateDoc].documentDate,
                        documentSubcategoryId: reqArr[this.countCreateDoc].documentSubcategoryId,
                        documentSystemId: reqArr[this.countCreateDoc].documentSystemId,
                        documentTypeId: reqArr[this.countCreateDoc].documentTypeId,
                        fileExtension: null,
                        fileName: reqArr[this.countCreateDoc].fileName,
                        publicationDate: reqArr[this.countCreateDoc].publicationDate,
                        Ssn: reqArr[this.countCreateDoc].Ssn,
                        stafraentIslandId: null,
                        submitToStafraentIsland: reqArr[this.countCreateDoc].submitToStafraentIsland
                            ? reqArr[this.countCreateDoc].submitToStafraentIsland
                            : false,
                        timeStamp: null,
                        title: reqArr[this.countCreateDoc].title,
                        svid: reqArr[this.countCreateDoc].svid,
                        deild: reqArr[this.countCreateDoc].deild,
                        submitToPortalLSR: reqArr[this.countCreateDoc].submitToPortalLSR,
                    },
                    this.files[this.files.findIndex((x) => x.name == reqArr[this.countCreateDoc].fileName)],
                    false,
                    false,
                    false
                )
                .subscribe((res) => {
                    this.countCreateDoc += 1;
                    // If there was an error we push it to the error array.
                    if (!res.createDocSuccess || !res.stafraentIslandSuccess) {
                        this.createDocErrors.push(this.files[0].name);
                    }
                    // If all jobs are finished
                    if (this.countCreateDoc == reqArr.length) {
                        this.formEnabled = true;
                        this.form.enable();
                        if (this.createDocErrors.length > 0) {
                            let errorForm = this.formBuilder.group({
                                title: 'Villur',
                                message:
                                    this.localeService.localizeNumber(this.createDocErrors.length) +
                                    (this.createDocErrors.length == 1 ? ' skjal lenti á villu' : ' skjöl lentu á villu') +
                                    ". Vinsamlegast hafið samband við <a href='atvik-og-beidnir/tolvuadstod-og-breytingabeidnir/senda-beidni'>kerfisstjórn</a> sem getur rakið vandamálið.",
                                icon: {
                                    show: true,
                                    name: 'heroicons_outline:exclamation',
                                    color: 'warn',
                                },
                                actions: {
                                    confirm: {
                                        show: true,
                                        label: 'Loka',
                                        color: 'warn',
                                    },
                                    cancel: {
                                        show: false,
                                        label: 'Hætta við',
                                    },
                                },
                                dismissible: true,
                            });

                            // Open the dialog and save the reference of it
                            const errorDialogRef = this._fuseConfirmationService.open(errorForm.value);

                            // Subscribe to afterClosed from the dialog reference
                            errorDialogRef.afterClosed().subscribe((result) => {
                                this.router.navigate(['/skjalasafn/skoda-skjol']);
                            });
                        } else {
                            this.router.navigate(['/skjalasafn/skoda-skjol']);
                            this.alertsService.changeAlertShowIcon(true);
                            this.alertsService.changeAlertSuccessMessage(
                                this.localeService.localizeNumber(this.countCreateDoc) +
                                    (this.countCreateDoc == 1 ? ' skjal hefur' : ' skjöl hafa') +
                                    ' verið skráð'
                            );
                            this.alertsService.changeAlertSuccessShow(true);
                        }
                    }
                    // If there are still documents to be uploaded we continue to add the documents.
                    else {
                        this.syncCallAddBref(reqArr);
                    }
                });
        }
    }

    //#endregion

    //#region Attachements

    onSelect(event) {
        this.validFilesAgainstKria = false;
        this.files = [];
        this.files.push(...event.addedFiles);
    }

    onRemove(event) {
        this.files.splice(this.files.indexOf(event), 1);
    }

    checkFilesAgainstKria() {
        this.brefLoggurValinnHopur.forEach((bref) => {
            if (!this.files.some((x) => x.name == bref.slod)) {
                this.validFilesAgainstKria = false;
                this.alertsService.changealertHeader('Villa kom upp');
                this.alertsService.changeAlertErrorMessage('Skrár í bréfabunka samsvara ekki þeim skrám sem þú hefur valið');
                this.alertsService.changeAlertErrorShow(true);
            } else {
                this.validFilesAgainstKria = true;
            }
        });
    }

    checkIfPdfFiles() {
        this.form.patchValue({
            generalInfo: {
                ...this.form.get('generalInfo').value,
                submitToStafraentIsland: false,
            },
        });
        this.pdfFiles = true;
        this.files.forEach((x) => {
            if (x.type != 'application/pdf') {
                this.pdfFiles = false;
            }
        });
    }

    viewDocumentDetailsList() {
        const dialogRef = this.dialog.open(DialogDocumentsKriaOverviewList, {
            data: {
                autoFocus: false,
                restoreFocus: false,
                disableClose: true,
                data: this.brefLoggurValinnHopur,
            },
            width: '1200px',
        });
        dialogRef.afterClosed().subscribe((result) => {});
    }

    //#endregion

    storeSelectedValues() {
        const values: DocumentsDivisionDefaultValues = {
            documentType: this.generalInfoComponent.form.get('documentTypeId').value,
            documentSystem: this.form.get('documentSystemId').value,
            documentCategory: this.generalInfoComponent.form.get('documentCategoryId').value,
            documentSubcategory: this.generalInfoComponent.form.get('documentSubcategoryId').value,
            division: this.generalInfoComponent.form.get('svid').value,
            department: this.generalInfoComponent.form.get('deild').value,
            defaultValueSelection: this.generalInfoComponent.form.get('defaultValue').value,
        };
        localStorage.setItem('documentsDivisionDefaultValues', JSON.stringify(values));
    }
}
